import React, { useState, useEffect } from 'react';
import { XMarkIcon, ArrowLeftIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { DataFileRowsResponse } from '../../types';
import ApiService from '../../services/api';
import DAPTable, { DAPTableColumn, DAPTableRow } from '../DAPTable';

interface DataModalProps {
  isOpen: boolean;
  onClose: () => void;
  dataFileId: number | null;
  dataFileName?: string;
}

const DataModal: React.FC<DataModalProps> = ({ 
  isOpen, 
  onClose, 
  dataFileId, 
  dataFileName = "Data File" 
}) => {
  const [data, setData] = useState<DataFileRowsResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState<number>(1);
  const [limit] = useState<number>(50);
  const [editedRows, setEditedRows] = useState<Record<string, any>[]>([]);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [saveSuccess, setSaveSuccess] = useState<boolean>(false);
  const [columns, setColumns] = useState<DAPTableColumn[]>([]);

  useEffect(() => {
    if (isOpen && dataFileId) {
      fetchData();
    }
  }, [isOpen, dataFileId, page, limit]);

  const fetchData = async () => {
    if (!dataFileId) return;

    setIsLoading(true);
    setError(null);
    
    try {
      const response = await ApiService.getDataFileRows(dataFileId, page, limit, true);
      setData(response);
      
      // Generate columns dynamically based on the response
      if (response.columns && response.columns.length > 0) {
        const tableColumns: DAPTableColumn[] = response.columns.map(col => ({
          key: col,
          label: col,
          render: (value, row) => (
            <input
              type="text"
              className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              defaultValue={value}
              onChange={(e) => handleCellEdit(row, col, e.target.value)}
            />
          )
        }));
        setColumns(tableColumns);
      }
      
      // Initialize edited rows with the current data
      setEditedRows(response.rows);
    } catch (err) {
      console.error('Error fetching data file rows:', err);
      setError('Failed to fetch data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCellEdit = (row: Record<string, any>, columnKey: string, newValue: string) => {
    // Find the row in editedRows and update the value
    const updatedRows = editedRows.map(r => {
      if (r === row) {
        return { ...r, [columnKey]: newValue };
      }
      return r;
    });
    
    setEditedRows(updatedRows);
  };

  const handleSave = async () => {
    if (!dataFileId) return;
    
    setIsSaving(true);
    setSaveSuccess(false);
    
    try {
      await ApiService.updateDataFileRows(dataFileId, editedRows);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving data file rows:', err);
      setError('Failed to save changes. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handlePreviousPage = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };

  const handleNextPage = () => {
    if (data?.has_more) {
      setPage(page + 1);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-xl w-11/12 max-w-7xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-xl font-semibold">
            {dataFileName} - View Data
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded-full hover:bg-gray-200 transition-colors"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-auto p-4">
          {isLoading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" data-testid="loading-spinner"></div>
            </div>
          ) : error ? (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          ) : data && data.rows.length > 0 ? (
            <DAPTable
              showSaveButton={false}
              showValidateButton={false}
              columns={columns}
              data={data.rows}
              className="w-full"
            />
          ) : (
            <div className="text-center text-gray-500 py-8">No data available</div>
          )}
        </div>
        
        {/* Footer with pagination and actions */}
        <div className="border-t p-4 flex justify-between items-center">
          {/* Pagination */}
          <div className="flex items-center space-x-4">
            <button
              onClick={handlePreviousPage}
              disabled={page <= 1}
              className={`p-2 rounded ${
                page <= 1
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-blue-600 hover:bg-blue-100'
              }`}
            >
              <ArrowLeftIcon className="w-5 h-5" />
            </button>
            
            <span className="text-sm">
              Page {page}
              {data?.total_pages ? ` of ${data.total_pages}` : ''}
              {data?.total_rows ? ` (${data.total_rows} rows total)` : ''}
            </span>
            
            <button
              onClick={handleNextPage}
              disabled={!data?.has_more}
              className={`p-2 rounded ${
                !data?.has_more
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-blue-600 hover:bg-blue-100'
              }`}
            >
              <ArrowRightIcon className="w-5 h-5" />
            </button>
          </div>
          
          {/* Actions */}
          <div className="flex items-center space-x-2">
            {saveSuccess && (
              <span className="text-green-600 text-sm">Changes saved successfully!</span>
            )}
            
            {/* <button
              onClick={handleSave}
              disabled={isSaving}
              className={`px-4 py-2 rounded ${
                isSaving
                  ? 'bg-blue-300 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>*/}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataModal;
